/**
 * Shared confirmation dialog — replaces browser confirm()/alert() popups.
 * Use with the useConfirmDialog() hook for a clean imperative API.
 */
import type { ReactNode } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';

export interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: string;
  /**
   * Optional rich content rendered ABOVE the message (e.g. the structured
   * pre-commit warning tables — SubmitWarningsContent). When present the
   * dialog widens to `sm` so tables have room. Backward compatible: omit for
   * the classic text-only dialog.
   */
  body?: ReactNode;
  confirmLabel?: string;
  confirmColor?: 'warning' | 'error' | 'primary';
  onConfirm: () => void;
  /**
   * Optional middle action (rendered between Cancel and Confirm). When set, a
   * third button appears so a dialog can offer two distinct destructive paths
   * (e.g. "Delete only" vs "Delete + release live schedule"). Backward
   * compatible — omit all three to keep the classic Cancel/Confirm dialog.
   */
  secondaryLabel?: string;
  onSecondary?: () => void;
  secondaryColor?: 'warning' | 'error' | 'primary';
  /** Alert mode: hide the Cancel button so the only way out is the confirm
   *  ("OK") action — for must-acknowledge notices, not yes/no choices. */
  hideCancel?: boolean;
}

export const CONFIRM_INITIAL: ConfirmDialogState = {
  open: false, title: '', message: '', onConfirm: () => {},
};

interface ConfirmDialogProps {
  state: ConfirmDialogState;
  onClose: () => void;
}

export const ConfirmDialog = ({ state, onClose }: ConfirmDialogProps) => (
  <Dialog open={state.open} onClose={onClose} maxWidth={state.body ? 'sm' : 'xs'} fullWidth
    PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, pb: 0 }}>
      <WarningAmber sx={{ color: `${state.confirmColor ?? 'warning'}.main` }} />
      {state.title}
    </DialogTitle>
    <DialogContent>
      {state.body}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
        {state.message}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      {!state.hideCancel && (
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
      )}
      {state.secondaryLabel && state.onSecondary && (
        <Button
          variant="outlined"
          color={state.secondaryColor ?? 'warning'}
          onClick={() => { onClose(); state.onSecondary?.(); }}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {state.secondaryLabel}
        </Button>
      )}
      <Button
        variant="contained"
        color={state.confirmColor ?? 'warning'}
        onClick={() => { onClose(); state.onConfirm(); }}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {state.confirmLabel ?? 'Confirm'}
      </Button>
    </DialogActions>
  </Dialog>
);
